import { useState, useEffect } from 'react';
import { api } from '../api';
import { Card, Pill, Table, THead, TH, TR, TD, Empty, Button, Input, fmtR, fmtPct, fmtDate } from './ui';

/**
 * Earned-value panel for a project (cumulative, lifetime).
 * Shows the three lenses — earned / billed / cost — their gaps, CPI,
 * a % complete bar, and the milestone list with quick add.
 */
export default function ProjectEarnedValuePanel({ projectId }) {
  const [ev, setEv] = useState(null);
  const [adding, setAdding] = useState(false);
  const [ms, setMs] = useState({ name: '', value: '', weight: '', planned_date: '' });
  const [invoice, setInvoice] = useState({ amount: '', number: '', issued_date: '' });
  const [msg, setMsg] = useState(null);

  const load = () => projectId && api.projects.earnedValue(projectId).then(setEv).catch(() => setEv(null));
  useEffect(load, [projectId]);

  if (!projectId) return null;
  if (!ev) return <Card title="Earned value"><Empty>Loading…</Empty></Card>;

  const cpiTone = ev.cpi == null ? 'neutral' : ev.cpi >= 1 ? 'ok' : 'danger';

  const addMilestone = async () => {
    if (!ms.name) return;
    setAdding(true); setMsg(null);
    try {
      await api.projects.addMilestone(projectId, {
        name: ms.name, value: parseFloat(ms.value) || 0,
        weight: parseFloat(ms.weight) || 0, planned_date: ms.planned_date || null,
        status: 'planned',
      });
      setMs({ name: '', value: '', weight: '', planned_date: '' });
      load();
    } catch (e) { setMsg({ kind: 'err', text: e.message }); }
    finally { setAdding(false); }
  };

  const addInvoice = async () => {
    if (!invoice.amount) return;
    setMsg(null);
    try {
      await api.projects.addInvoice(projectId, {
        amount: parseFloat(invoice.amount) || 0,
        number: invoice.number || null, issued_date: invoice.issued_date || null,
        status: 'issued',
      });
      setInvoice({ amount: '', number: '', issued_date: '' });
      load();
    } catch (e) { setMsg({ kind: 'err', text: e.message }); }
  };

  const Stat3 = ({ label, value, tone }) => (
    <div className="flex-1">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-lg font-semibold nums ${tone || 'text-slate-100'}`}>{value}</div>
    </div>
  );

  return (
    <Card
      title="Earned value"
      subtitle={`${ev.contract_basis} · ${ev.pct_complete_basis}-based progress`}
      action={<Pill tone={cpiTone}>CPI {ev.cpi ?? '—'}</Pill>}
    >
      {/* % complete bar */}
      <div className="mb-4">
        <div className="flex justify-between text-xs text-slate-500 mb-1">
          <span>{fmtPct(ev.pct_complete)} complete</span>
          <span>{ev.tracked_hours}h / {ev.scoped_hours}h scoped</span>
        </div>
        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
          <div className="h-full bg-brand-500 rounded-full" style={{ width: `${Math.min(ev.pct_complete, 100)}%` }} />
        </div>
      </div>

      <div className="flex gap-4 mb-4">
        <Stat3 label="Earned value" value={ev.earned_value != null ? fmtR(ev.earned_value) : '—'} />
        <Stat3 label="Invoiced" value={ev.invoiced != null ? fmtR(ev.invoiced) : '—'} />
        <Stat3 label="Actual cost" value={ev.actual_cost != null ? fmtR(ev.actual_cost) : '—'} />
      </div>

      <div className="grid grid-cols-3 gap-2 text-xs mb-4">
        {[
          ['EV − billed', ev.ev_minus_billed, 'delivered, not yet billed'],
          ['EV − cost', ev.ev_minus_cost, 'margin earned to date'],
          ['Billed − cost', ev.billed_minus_cost, 'cash margin'],
        ].map(([l, v, hint]) => (
          <div key={l} className="bg-slate-900/50 rounded-md px-2 py-1.5">
            <div className="text-slate-500">{l}</div>
            <div className={`font-semibold nums ${v == null ? 'text-slate-500' : v >= 0 ? 'text-emerald-600' : 'text-brand-500'}`}>
              {v != null ? fmtR(v) : '—'}
            </div>
            <div className="text-[10px] text-slate-600">{hint}</div>
          </div>
        ))}
      </div>

      {ev.cost_is_estimated && (
        <p className="text-[11px] text-amber-600 mb-3">Cost partly estimated (33% proxy) — some devs lack captured CTC.</p>
      )}

      {/* Milestones */}
      <div className="border-t border-slate-700 pt-3">
        <div className="text-xs font-medium text-slate-400 mb-2">Milestones ({ev.milestones.length})</div>
        {ev.milestones.length > 0 && (
          <Table>
            <THead><TH>Name</TH><TH align="right">Value</TH><TH align="right">Weight</TH><TH>Planned</TH><TH>Status</TH></THead>
            <tbody>
              {ev.milestones.map((m, i) => (
                <TR key={i}>
                  <TD>{m.name}</TD>
                  <TD align="right" mono>{fmtR(m.value)}</TD>
                  <TD align="right" mono>{m.weight || '—'}</TD>
                  <TD muted mono>{fmtDate(m.planned_date)}</TD>
                  <TD><Pill tone={m.status === 'complete' ? 'ok' : 'neutral'}>{m.status}</Pill></TD>
                </TR>
              ))}
            </tbody>
          </Table>
        )}
        <div className="flex flex-wrap gap-2 mt-2">
          <Input value={ms.name} onChange={(e) => setMs({ ...ms, name: e.target.value })} placeholder="Milestone" className="flex-1 min-w-32" />
          <Input type="number" value={ms.value} onChange={(e) => setMs({ ...ms, value: e.target.value })} placeholder="Value" className="w-24" />
          <Input type="number" value={ms.weight} onChange={(e) => setMs({ ...ms, weight: e.target.value })} placeholder="Weight%" className="w-24" />
          <Input type="date" value={ms.planned_date} onChange={(e) => setMs({ ...ms, planned_date: e.target.value })} className="w-36" />
          <Button size="sm" variant="secondary" onClick={addMilestone} loading={adding}>Add</Button>
        </div>
      </div>

      {/* Manual invoice */}
      <div className="border-t border-slate-700 pt-3 mt-3">
        <div className="text-xs font-medium text-slate-400 mb-2">Log invoice ({ev.invoice_count})</div>
        <div className="flex flex-wrap gap-2">
          <Input type="number" value={invoice.amount} onChange={(e) => setInvoice({ ...invoice, amount: e.target.value })} placeholder="Amount" className="w-32" />
          <Input value={invoice.number} onChange={(e) => setInvoice({ ...invoice, number: e.target.value })} placeholder="Number" className="w-32" />
          <Input type="date" value={invoice.issued_date} onChange={(e) => setInvoice({ ...invoice, issued_date: e.target.value })} className="w-36" />
          <Button size="sm" variant="secondary" onClick={addInvoice}>Log</Button>
        </div>
        <p className="text-[10px] text-slate-600 mt-2">Manual until the connector invoice feed lands.</p>
      </div>

      {msg && <p className={`text-xs mt-3 ${msg.kind === 'ok' ? 'text-emerald-600' : 'text-brand-500'}`}>{msg.text}</p>}
    </Card>
  );
}
